// src/app/components/admin/global-settings-page.tsx
//
// Upmode Global Settings — a full page whose SECTIONS are chosen from a left
// navigation pane. It consolidates the platform-wide admin areas that used to
// be separate items in the top-nav menu (User Management, Associate Brands,
// Brand API Keys) and adds a Platform section for global switches like the
// Performance data source. Admin-only: the route is gated in App.tsx. Each
// embedded section is the same self-contained component the standalone admin
// pages render — no duplicated logic; this page is the shell plus the nav.
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// The nav is Kit 07 item 147 (`SettingsNav`), the mobile pane is Kit 03 item
// 54 (`Drawer`), and `data-kit` is on the root.
//
// Three things the swap fixed:
//
//   * Each section carried its OWN accent colour (`--alpha-teal`,
//     `--spectral-violet`, `--signal-blue`, `--chart-4`) on the active row's
//     left border and its icon. Four accents in a four-row nav is four
//     meanings where there is one: which row is selected. Item 147 draws one
//     selection treatment.
//   * The mobile drawer was hand-rolled: a fixed scrim at `z-index: 45`, a
//     panel at 46, a body-scroll lock, and no focus trap, no Escape, no
//     return-focus. Two literal z-indexes outside the named ladder and none of
//     Phase 2's dismiss contract. `Drawer` owns all of it.
//   * `rgba(0,0,0,0.5)` was a scrim colour typed by hand beside a `--scrim`
//     token that already existed.
//
// The Platform section still renders PlatformSettingsSection, the card index
// that holds approved email recipients, self-service signup, report share-link
// expiry and the performance data source. The kit swap is the frame only; not
// one platform switch moved, and none of them lost a route in.
//
// The shell claims `min-height: 100%`, never 100vh. This page renders BELOW
// the shared TopNavBar inside a shell that is already full height, so a second
// 100vh claim used to make the document permanently taller than the viewport
// by the height of that nav — a dead band you could always scroll into.

import { useState, type ReactNode } from "react";
import {
  SlidersHorizontal, Users, Tag, KeyRound, PanelLeft, Activity, ScrollText,
} from "lucide-react";

import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { AdminUsersPage } from "@/app/components/login/admin/admin-users-page";
import { AdminBrandAccessPage } from "@/app/components/login/admin/admin-brand-access-page";
import { AdminBrandKeysPage } from "@/app/components/login/admin/admin-brand-keys-page";
import { PlatformSettingsSection } from "./platform-settings-section";
import { Button, Drawer, OverlayProvider, SettingsNav } from "../kit";
import type { SettingsNavItem } from "../kit";
import { HealthDetailsSection } from "./health-details-section";
import { LogsSection } from "./logs-section";

export type GlobalSettingsSection = "platform" | "users" | "brands" | "keys" | "health" | "logs";

interface SectionDef extends SettingsNavItem {
  id: GlobalSettingsSection;
  render: () => ReactNode;
}

const SECTIONS: SectionDef[] = [
  {
    id: "platform",
    label: "Platform",
    icon: SlidersHorizontal,
    // Its own index-and-detail shell: the platform switches outgrew a single
    // stacked page. See platform-settings-section.tsx.
    render: () => <PlatformSettingsSection />,
  },
  { id: "users",  label: "User Management",  icon: Users,    render: () => <AdminUsersPage /> },
  { id: "brands", label: "Associate Brands", icon: Tag,      render: () => <AdminBrandAccessPage /> },
  { id: "keys",   label: "Brand API Keys",   icon: KeyRound, render: () => <AdminBrandKeysPage /> },
  // Read-only operational views (UPM-169): what build is running, whether its
  // dependencies answer, which migrations are applied, and the captured logs.
  { id: "health", label: "Health Details",   icon: Activity,   render: () => <HealthDetailsSection /> },
  { id: "logs",   label: "Logs",             icon: ScrollText, render: () => <LogsSection /> },
];

interface GlobalSettingsPageProps {
  onNavigate?: (page: string) => void;
  initialSection?: GlobalSettingsSection;
}

export function GlobalSettingsPage({ onNavigate, initialSection = "platform" }: GlobalSettingsPageProps) {
  const [active, setActive] = useState<GlobalSettingsSection>(initialSection);
  const { isDesktop } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeSection = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  const nav = (
    <div className="upm-settings-shell__nav">
      <header className="upm-settings-shell__nav-head">
        <h2 className="upm-settings-shell__nav-title">Global Settings</h2>
        <span className="k-meta">Admin, platform-wide</span>
      </header>

      <SettingsNav
        label="Global settings sections"
        items={SECTIONS}
        value={active}
        onChange={(id) => { setActive(id as GlobalSettingsSection); setDrawerOpen(false); }}
      />

      {/* Onboard is a flow, not a section, so it stays a quick action rather
          than becoming a fifth nav row that leaves the page. */}
      <div className="upm-settings-shell__nav-foot">
        <Button variant="secondary" onClick={() => onNavigate?.("onboarding")}>
          Onboard a customer
        </Button>
      </div>
    </div>
  );

  return (
    // The provider owns the one scrim and the Escape stack. Without it the
    // drawer still opens, but nothing dims the page behind it.
    <OverlayProvider className="upm-settings-shell" contained>
      <div className="upm-settings-shell__grid" data-kit>
        {isDesktop ? (
          <aside className="upm-settings-shell__aside">{nav}</aside>
        ) : (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            title="Global settings sections"
          >
            {nav}
          </Drawer>
        )}

        <main className="upm-settings-shell__main">
          {!isDesktop && (
            <div className="upm-settings-shell__strip">
              <Button variant="ghost" icon={PanelLeft} onClick={() => setDrawerOpen(true)}>
                {activeSection.label}
              </Button>
            </div>
          )}

          {activeSection.render()}
        </main>
      </div>
    </OverlayProvider>
  );
}

export default GlobalSettingsPage;
